import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { SnackbarKey } from 'notistack';

import { useSnackbar } from 'src/utils/hooks';
import { useFilterContext } from 'src/context/FilterContext';
import { usePaginationContext } from 'src/context/PaginationContext';

import { confirmUserRequest } from '../requests/confirmUserRequest';
import { QUERY_NAME } from '../constants';
import { FetchUserRequestsResult } from '../typings';

type UseConfirmUserRequestResult = UseMutationResult<
  void,
  Error,
  string,
  UseConfrimUserRequestContext
>;

type UseConfrimUserRequestContext = {
  previousUserRequests: FetchUserRequestsResult | undefined;
  successSnackbarKey: SnackbarKey;
};

export const useConfirmUserRequest = (): UseConfirmUserRequestResult => {
  const queryClient = useQueryClient();

  const { enqueueErrorSnackbar, enqueueSuccessSnackbar, closeSnackbar } =
    useSnackbar();

  const { filter } = useFilterContext();
  const { pagination } = usePaginationContext();

  const result = useMutation<void, Error, string, UseConfrimUserRequestContext>(
    confirmUserRequest,
    {
      onMutate: async (requestId: string) => {
        await queryClient.cancelQueries([
          QUERY_NAME.userRequests,
          { filter, pagination },
        ]);

        const previousUserRequests =
          queryClient.getQueryData<FetchUserRequestsResult>([
            QUERY_NAME.userRequests,
            { filter, pagination },
          ]);

        const nextPageUserRequests =
          queryClient.getQueryData<FetchUserRequestsResult>([
            QUERY_NAME.userRequests,
            {
              filter,
              pagination: {
                rowsPerPage: pagination.rowsPerPage,
                page: pagination.page + 1,
                skip: (pagination.page + 1) * pagination.rowsPerPage,
              },
            },
          ]);

        if (previousUserRequests) {
          queryClient.setQueryData<FetchUserRequestsResult>(
            [QUERY_NAME.userRequests, { filter, pagination }],
            () => {
              const newUserRequests = previousUserRequests.data.filter(
                (userRequest) => userRequest.id !== requestId
              );

              if (nextPageUserRequests?.data[0]) {
                newUserRequests.push(nextPageUserRequests.data[0]);
              }

              return {
                count: previousUserRequests.count - 1,
                data: newUserRequests,
              };
            }
          );
        }

        const successSnackbarKey = enqueueSuccessSnackbar({
          message: 'page.users.user_requests.snackbar.user_confirmed',
        });

        return { previousUserRequests, successSnackbarKey };
      },
      onError: (error, _, context) => {
        if (context?.previousUserRequests) {
          queryClient.setQueryData<FetchUserRequestsResult>(
            [QUERY_NAME.userRequests, { filter, pagination }],
            context.previousUserRequests
          );
        }

        closeSnackbar(context?.successSnackbarKey);
        enqueueErrorSnackbar({ message: error.message });
      },
      onSettled: () => {
        queryClient.invalidateQueries(QUERY_NAME.userRequests);
      },
    }
  );

  return result;
};
